import {Component, computed, EventEmitter, Inject, input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';

import {Survey, SurveyFilter} from '../../../api/models/survey';
import {User} from '../../../api/models/user';

import {SurveyService} from '../../../api/services/survey.service';

import {
  ConfirmationDialogComponent
} from '../../general-purpose/dialogs/confirmation-dialog/confirmation-dialog.component';
import {SurveyCreateComponent} from '../survey-create/survey-create.component';
import {SurveyEditComponent} from '../survey-edit/survey-edit.component';

import {DATE_FORMAT, SURVEY_DEFAULT_FILTER} from '../../../api/constants';

import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {SurveyListElementComponent} from './survey-list-element/survey-list-element.component';
import {Dialog} from "@angular/cdk/dialog";
import {
  WaButtonDirective,
  WaCalloutDirective,
  WaIconDirective,
  WaScrollerDirective,
  WaToastService
} from "angular-awesome";
import {rxResource} from "@angular/core/rxjs-interop";

@Component({
    selector: 'app-survey-list',
    templateUrl: './survey-list.component.html',
    styleUrls: ['./survey-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SurveyListElementComponent, WaButtonDirective, WaIconDirective, WaCalloutDirective, WaScrollerDirective]
})
export class SurveyListComponent implements OnInit {

  defaultFilter = SURVEY_DEFAULT_FILTER;

  readonly user = input<User | null>(null);
  @Output() selectedEvent = new EventEmitter<Survey | null>();

  survey_params = computed(() => ({
    skip: 0,
    limit: 100,
    filter: this.filter
  }));
  surveys = rxResource({
    params: this.survey_params,
    stream: ({params}) => this.surveyService.getAllSurveys(params.skip, params.limit, params.filter)
  });

  // SurveyView as a readonly view sorted by voting start date, then voting end date
  surveysView = computed<Survey[] | null>( () => this.surveys.hasValue() ? this.surveys.value().data.toSorted( (a,b) => {
    const a_start = new Date(a.begin);
    const b_start = new Date(b.begin);
    if (a_start.getTime() - b_start.getTime() !== 0) { return a_start.getTime() - b_start.getTime(); }

    const a_end = new Date(a.end);
    const b_end = new Date(b.end);
    return a_end.getTime() - b_end.getTime();
    }) : null);


  filter: SurveyFilter = this.defaultFilter;

  isMobile: boolean = false;

  constructor(private router: Router,
              private dialog: Dialog,
              private toastService: WaToastService,
              private surveyService: SurveyService,
              private breakpointObserver: BreakpointObserver,
              @Inject(DATE_FORMAT) public dateFormat: string) {}

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobile = result.matches;
    });
  }

  gotoAssignment(survey: Survey | null): void {
    if (!survey) return;
    this.router.navigateByUrl(`surveys/${survey.id}/assignment`);
  }

  async publishAssignment(survey: Survey | null): Promise<void> {
    if (!survey || survey.published)
      return;

    const ref = this.dialog.open<boolean>(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        title: 'Publication confirmation',
        message: `Are you sure that you want to publish the
                  current assignment for survey ${survey.name}?`,
        cancel_button: true
      }
    });

    ref.closed.subscribe(async data => {
      if (!data) return;
      try {
        const updatedSurvey = await this.surveyService.publish(survey.id);
      } catch (error: any) {
        this.toastService.danger('Could not publish survey: ' + error.message);
      }
    });
  }

  edit(survey: Survey | null): void {
    if (!survey) return;

    const ref = this.dialog.open<Survey>(SurveyEditComponent, {
      disableClose: true,
      data: { survey }
    });

    ref.closed.subscribe(survey => {
      if (!survey) return;

    //   const surveyIndex = this.surveys().findIndex(other => {
    //     return other.id === survey.id;
    //   });
    //
    //   if (surveyIndex !== -1)
    //     this.surveys.update(items => items.map((item,index) => index === surveyIndex ? survey : item));
     });
  }

  remove(survey: Survey | null): void {
    if (!survey) return;
    const surveyName = survey.name;

    const ref = this.dialog.open<boolean>(ConfirmationDialogComponent, {
      data: {
        title: 'Confirm deletion',
        message: `Are you sure that you want to delete survey "${survey.name}?"`,
        cancel_button: true
      }
    });

    ref.closed.subscribe(data => {
      if (!data) return;
      this.surveyService.delete(survey.id).subscribe({
        next: () => {
          //this.selectedSurvey = null;
          this.surveys.reload();
          this.toastService.success(`survey ${surveyName} has been deleted.`);
        },
        error: err => {
          this.toastService.danger('Could not delete survey.' + err.message);
        }
      });
    });
  }

  submitExclusions(survey: Survey): void {
    if (!survey) return;
    this.router.navigateByUrl(`surveys/${survey.id}/preferences`);
  }

  gotoDashboard(survey: Survey): void {
    if (!survey) return;
    this.router.navigateByUrl(`surveys/${survey.id}/dashboard`);
  }

  showCreateDialog(): void {
    const user = this.user();
    if (!user || !user.isAdmin)
      return;

    const ref = this.dialog.open<Survey>(SurveyCreateComponent, {
      disableClose: true
    });

    ref.closed.subscribe(survey => {
      if (!survey) return;
    });
  }
}
